import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '@/lib/contexts/auth-context'
import { Eye, EyeOff, Loader2, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { FormField, FormPage, formLinkClass } from '@/components/landing/EditorialForm'
import { agents } from '@/components/landing/AITeamOrbit'

export default function LoginPage() {
  const navigate = useNavigate()
  const { login, isAuthenticated } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await login(formData)
    } catch (err) {
      setError((err as Error).message || 'Invalid email or password')
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  // Demo accounts organized by company
  const demoCompanies = [
    {
      name: 'Default Tenant',
      subdomain: 'default',
      industry: null,
      accounts: [
        { email: 'admin@templatean.com', password: 'Admin123!', label: 'Super Admin', role: 'Administrator' },
        { email: 'test@test.com', password: 'test123', label: 'Test User', role: 'User' },
        { email: 'demo@demo.com', password: 'demo123', label: 'Demo User', role: 'User' },
        { email: 'user@example.com', password: 'user123', label: 'Example User', role: 'User' },
      ]
    },
    {
      name: 'ACME Corporation',
      subdomain: 'acme',
      industry: 'General',
      accounts: [
        { email: 'admin@acme.com', password: 'AcmeAdmin123!', label: 'Admin', role: 'Administrator' },
        { email: 'ceo@acme.com', password: 'AcmeCeo123!', label: 'CEO', role: 'Executive' },
        { email: 'coo@acme.com', password: 'AcmeCoo123!', label: 'COO', role: 'Executive' },
        { email: 'marketing.manager@acme.com', password: 'AcmeMkt123!', label: 'Marketing Mgr', role: 'Marketing' },
        { email: 'marketing.specialist@acme.com', password: 'AcmeMkt123!', label: 'Marketing Spec', role: 'Marketing' },
        { email: 'sales.director@acme.com', password: 'AcmeSales123!', label: 'Sales Director', role: 'Sales' },
        { email: 'sales.rep@acme.com', password: 'AcmeSales123!', label: 'Sales Rep', role: 'Sales' },
        { email: 'it.manager@acme.com', password: 'AcmeIT123!', label: 'IT Manager', role: 'Engineering' },
        { email: 'developer@acme.com', password: 'AcmeIT123!', label: 'Developer', role: 'Engineering' },
        { email: 'hr.manager@acme.com', password: 'AcmeHR123!', label: 'HR Manager', role: 'Human Resources' },
      ]
    },
    {
      name: 'Morrison & Sterling LLP',
      subdomain: 'morrison-sterling',
      industry: 'Legal',
      accounts: [
        { email: 'robert.chen@morrison-sterling.com', password: 'LegalDemo123!', label: 'Managing Partner', role: 'Partner' },
        { email: 'jennifer.martinez@morrison-sterling.com', password: 'LegalDemo123!', label: 'Senior Associate', role: 'Associate' },
        { email: 'david.park@morrison-sterling.com', password: 'LegalDemo123!', label: 'Junior Associate', role: 'Associate' },
        { email: 'maria.santos@morrison-sterling.com', password: 'LegalDemo123!', label: 'Paralegal', role: 'Paralegal' },
        { email: 'amanda.foster@morrison-sterling.com', password: 'LegalDemo123!', label: 'Legal Secretary', role: 'Secretary' },
      ]
    },
    {
      name: 'Meridian Capital',
      subdomain: 'meridian-capital',
      industry: 'Finance',
      accounts: [
        { email: 'michael.roberts@meridian-capital.com', password: 'FinanceDemo123!', label: 'Portfolio Manager', role: 'Manager' },
        { email: 'sarah.kim@meridian-capital.com', password: 'FinanceDemo123!', label: 'Research Analyst', role: 'Analyst' },
        { email: 'james.wilson@meridian-capital.com', password: 'FinanceDemo123!', label: 'Compliance Officer', role: 'Compliance' },
        { email: 'emily.chen@meridian-capital.com', password: 'FinanceDemo123!', label: 'Client Advisor', role: 'Advisor' },
        { email: 'alex.johnson@meridian-capital.com', password: 'FinanceDemo123!', label: 'Operations Analyst', role: 'Operations' },
      ]
    },
    {
      name: 'Guardian Insurance',
      subdomain: 'guardian-insurance',
      industry: 'Insurance',
      accounts: [
        { email: 'lloyd.harrison@guardian-insurance.com', password: 'InsuranceDemo123!', label: 'Underwriting Mgr', role: 'Manager' },
        { email: 'hartford.james@guardian-insurance.com', password: 'InsuranceDemo123!', label: 'Claims Analyst', role: 'Analyst' },
        { email: 'prudence.williams@guardian-insurance.com', password: 'InsuranceDemo123!', label: 'Compliance Officer', role: 'Compliance' },
        { email: 'sterling.davis@guardian-insurance.com', password: 'InsuranceDemo123!', label: 'Actuary', role: 'Actuary' },
        { email: 'brooks.taylor@guardian-insurance.com', password: 'InsuranceDemo123!', label: 'Policy Writer', role: 'Writer' },
      ]
    },
    {
      name: 'Regional Medical Center',
      subdomain: 'regional-medical',
      industry: 'Healthcare',
      accounts: [
        { email: 'dr.hippa@regional-medical.com', password: 'HealthDemo123!', label: 'Medical Director', role: 'Director' },
        { email: 'florence.night@regional-medical.com', password: 'HealthDemo123!', label: 'Clinical Doc Spec', role: 'Specialist' },
        { email: 'darwin.research@regional-medical.com', password: 'HealthDemo123!', label: 'Research Coord', role: 'Coordinator' },
        { email: 'galen.privacy@regional-medical.com', password: 'HealthDemo123!', label: 'HIPAA Officer', role: 'Compliance' },
        { email: 'clara.patient@regional-medical.com', password: 'HealthDemo123!', label: 'Patient Services', role: 'Services' },
      ]
    },
    {
      name: 'BioVantage Therapeutics',
      subdomain: 'biovantage',
      industry: 'Pharmaceutical',
      accounts: [
        { email: 'pasteur.lab@biovantage.com', password: 'PharmaDemo123!', label: 'Research Director', role: 'Director' },
        { email: 'fleming.reg@biovantage.com', password: 'PharmaDemo123!', label: 'Regulatory Affairs', role: 'Regulatory' },
        { email: 'curie.data@biovantage.com', password: 'PharmaDemo123!', label: 'Clinical Data Mgr', role: 'Manager' },
        { email: 'jenner.qa@biovantage.com', password: 'PharmaDemo123!', label: 'Quality Assurance', role: 'QA' },
        { email: 'salk.writer@biovantage.com', password: 'PharmaDemo123!', label: 'Medical Writer', role: 'Writer' },
      ]
    },
    {
      name: 'Strategos Consulting',
      subdomain: 'strategos',
      industry: 'Consulting',
      accounts: [
        { email: 'porter.strategy@strategos.com', password: 'ConsultDemo123!', label: 'Partner', role: 'Partner' },
        { email: 'drucker.analyst@strategos.com', password: 'ConsultDemo123!', label: 'Strategy Analyst', role: 'Analyst' },
        { email: 'kaplan.finance@strategos.com', password: 'ConsultDemo123!', label: 'Proposal Writer', role: 'Writer' },
        { email: 'covey.pm@strategos.com', password: 'ConsultDemo123!', label: 'Project Manager', role: 'PM' },
        { email: 'collins.content@strategos.com', password: 'ConsultDemo123!', label: 'Content Creator', role: 'Creator' },
      ]
    },
  ]

  const [selectedCompany, setSelectedCompany] = useState(0)

  // Demo rows sign straight in: this page is used constantly in development.
  const quickLogin = async (email: string, password: string) => {
    setFormData({ email, password })
    setError('')
    setIsLoading(true)
    try {
      await login({ email, password })
    } catch (err) {
      setError((err as Error).message || 'Invalid email or password')
    } finally {
      setIsLoading(false)
    }
  }

  const company = demoCompanies[selectedCompany]

  return (
    <LandingLayout>
      <FormPage
        title={['Welcome back.', 'Your brand is waiting.']}
        lede={
          <p>
            Sign in to your templates, agents and brand assets. New here?{' '}
            <Link to="/register" className={formLinkClass}>
              Start a free trial
            </Link>
            .
          </p>
        }
      >
        {/* One field per row, as asked: email, then password. */}
        <form onSubmit={handleSubmit} className="grid gap-3" aria-label="Sign in">
          {error && (
            <p role="alert" className="border-l-2 border-[#ee1d36] bg-[#ee1d36]/[0.06] px-4 py-3 text-sm text-[#b3001b] dark:text-[#ff8a99]">
              {error}
            </p>
          )}
          <FormField id="email" label="Email address">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="Your work email"
              className="landing-field"
            />
          </FormField>
          <FormField id="password" label="Password" className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={formData.password}
              onChange={handleChange}
              placeholder="Your password"
              className="landing-field pr-14"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute inset-y-0 right-0 flex items-center px-4 text-[#6e6e6e] transition-colors hover:text-[var(--color-landing-gray-900)] dark:hover:text-white"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </FormField>

          <div className="mt-2 flex flex-wrap items-center justify-between gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
              <input type="checkbox" className="h-4 w-4 rounded-none accent-[var(--color-landing-accent)]" />
              Remember me
            </label>
            <Link to="/forgot-password" className={cn(formLinkClass, 'text-sm')}>
              Forgot password?
            </Link>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={isLoading}
              className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep w-full disabled:opacity-60 sm:w-auto"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Signing in
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>
            <span className="hidden px-2 text-sm sm:inline text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">or</span>
            <div className="grid w-full grid-cols-2 gap-3 sm:flex sm:w-auto">
              <button type="button" className="landing-cta landing-cta--outline landing-cta--md landing-btn-sweep gap-2.5">
                <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
                  <path fill="currentColor" d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z" />
                </svg>
                Microsoft
              </button>
              <button type="button" className="landing-cta landing-cta--outline landing-cta--md landing-btn-sweep gap-2.5">
                <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Google
              </button>
            </div>
          </div>
        </form>
      </FormPage>

      {/* Second section, as the reference's "Are you looking for us?" tiles:
          demo accounts in development, the agent team in production. */}
      <section aria-labelledby="login-secondary" className="pb-24 md:pb-32">
        <div className="landing-container">
          <div className="mx-auto max-w-6xl">
            {import.meta.env.DEV ? (
              <>
                <h2 id="login-secondary" className="landing-text-display-lg text-[var(--color-landing-gray-900)] dark:text-white">
                  Quick login.
                </h2>
                <div className="mt-8 flex max-w-5xl flex-wrap gap-2">
                  {demoCompanies.map((c, index) => (
                    <button
                      key={c.subdomain}
                      type="button"
                      onClick={() => setSelectedCompany(index)}
                      aria-pressed={index === selectedCompany}
                      className={cn(
                        'h-10 px-4 font-[family-name:var(--font-landing-subdisplay)] text-sm font-bold uppercase transition-colors',
                        index === selectedCompany
                          ? 'bg-[var(--color-landing-accent)] text-white'
                          : 'bg-[#0a0a0a]/5 text-[var(--color-landing-gray-700)] hover:bg-[#0a0a0a]/10 dark:bg-white/10 dark:text-[var(--color-landing-gray-300)] dark:hover:bg-white/15'
                      )}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
                <ul className="mt-6 grid max-w-5xl grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {company?.accounts.map((account) => (
                    <li key={account.email}>
                      <button
                        type="button"
                        disabled={isLoading}
                        onClick={() => quickLogin(account.email, account.password)}
                        className="group flex h-full w-full flex-col justify-between gap-6 bg-[#0a0a0a]/5 p-5 text-left transition-colors hover:bg-[var(--color-landing-accent)] hover:text-white disabled:opacity-60 dark:bg-white/10"
                      >
                        <span className="flex items-start justify-between gap-3">
                          <span className="font-[family-name:var(--font-landing-subdisplay)] text-lg font-bold uppercase leading-tight">
                            {account.label}
                          </span>
                          <ArrowRight className="mt-1 h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
                        </span>
                        <span>
                          <span className="block break-all text-sm opacity-75">{account.email}</span>
                          <span className="mt-1 block text-xs opacity-60">{account.role}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-sm text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                  Tenant {company?.subdomain}. Development builds only.
                </p>
              </>
            ) : (
              <>
                <h2 id="login-secondary" className="landing-text-display-lg text-[var(--color-landing-gray-900)] dark:text-white">
                  Six agents. One brand.
                </h2>
                <ul className="mt-8 grid max-w-5xl grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {agents.map((agent) => (
                    <li key={agent.name} className="flex items-center gap-4 bg-[#0a0a0a]/5 p-5 dark:bg-white/10">
                      <span className="h-14 w-14 shrink-0 overflow-hidden" style={{ backgroundColor: agent.color }}>
                        <img src={agent.avatar} alt="" className="h-full w-full translate-y-[6%] scale-110 object-cover" />
                      </span>
                      <span>
                        <span className="block font-[family-name:var(--font-landing-subdisplay)] text-lg font-bold uppercase leading-tight text-[var(--color-landing-gray-900)] dark:text-white">
                          {agent.name}
                        </span>
                        <span className="block text-sm text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                          {agent.role}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </section>
    </LandingLayout>
  )
}
